import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PanoramaViewer from './PanoramaViewer';

interface VirtualTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  propertyTitle?: string;
}

export default function VirtualTourModal({
  isOpen,
  onClose,
  imageUrl,
  propertyTitle,
}: VirtualTourModalProps) {
  const { t, i18n } = useTranslation(['property', 'common']);
  const direction = i18n.dir();

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={handleBackdropClick}
      dir={direction}
    >
      {/* Modal content */}
      <div className="relative w-full h-full max-w-7xl max-h-[90vh] m-4">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--pv-color-primary)] text-white text-sm font-medium">
              <i className="ri-panorama-2-line"></i>
              {t('virtualTour.badge', { defaultValue: '360°' })}
            </span>
            {propertyTitle && (
              <h2 className="text-white text-lg font-semibold truncate max-w-md">
                {propertyTitle}
              </h2>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
            <span className="text-sm font-medium hidden sm:inline">
              {t('virtualTour.exitFullscreen', { defaultValue: 'Exit' })}
            </span>
          </button>
        </div>

        {/* Panorama viewer */}
        <PanoramaViewer
          imageUrl={imageUrl}
          title={propertyTitle}
          autoRotate={true}
          showControls={true}
          height="100%"
          className="w-full h-full"
        />

        {/* Instructions overlay */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-4 px-4 py-2 rounded-full bg-black/50 text-white/80 text-xs">
            <span className="flex items-center gap-1.5">
              <i className="ri-drag-move-line"></i>
              {t('virtualTour.instructions.drag', { defaultValue: 'Drag to look around' })}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-zoom-in-line"></i>
              {t('virtualTour.instructions.scroll', { defaultValue: 'Scroll to zoom' })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
